import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { debug } from '../utils/logger';

const Header = ({ 
  user, 
  isLoggedIn,
  onLogout, 
  darkMode, 
  toggleTheme 
}) => {
  // Get user directly from AuthContext to ensure real-time updates
  const { user: authUser, fetchUserFromAPI } = useAuth();
  
  // Use AuthContext user if available, fallback to prop
  const currentUser = authUser || user;
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // React Router hooks
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get current page from React Router location
  const currentPage = location.pathname === '/' ? 'home' : location.pathname.slice(1);
  
  // Debug logging for credit updates
  useEffect(() => {
    if (currentUser?.credits !== undefined) {
      debug('Header: Current user credits', { credits: currentUser.credits });
    }
  }, [currentUser?.credits]);
  

  const navItems = [
    { id: 'create', label: 'Create' },
    { id: 'pricing', label: 'Pricing' },
    ...(isLoggedIn ? [
      { id: 'dashboard', label: 'Dashboard' },
      { id: 'my-creations', label: 'My Creations' }
    ] : [])
  ];

  // Close mobile menu when page changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [currentPage]);

  // Close mobile menu on window resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle navigation with mobile menu close
  const handleNavigation = (pageId) => {
    const path = pageId === 'home' ? '/' : `/${pageId}`;
    navigate(path);
    setMobileMenuOpen(false);
  };

  // Toggle mobile menu
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  return (
    <>
      <header className="app-header">
        <div className="header-container">
          {/* Logo */}
          <div className="logo" onClick={() => handleNavigation('home')}>
            <div className="logo-icon">MP</div>
            <span className="logo-text">Mint<span style={{color: '#007AFF'}}>Print</span>.ai</span>
          </div>

          {/* Desktop Navigation */}
          <nav className={`header-nav ${mobileMenuOpen ? 'mobile-open' : ''}`}>
            {navItems.map(item => (
              <button
                key={item.id}
                className={`nav-item ${currentPage === item.id ? 'active' : ''}`}
                onClick={() => handleNavigation(item.id)}
              >
                
                <span className="nav-label">{item.label}</span>
              </button>
            ))}
          </nav>

          {/* Mobile Menu Toggle */}
          <button 
            className={`mobile-menu-toggle ${mobileMenuOpen ? 'active' : ''}`}
            onClick={toggleMobileMenu}
            aria-label="Toggle mobile menu"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>

          {/* User Section */}
          <div className="header-user">
            {isLoggedIn ? (
              <>
                {/* Credits Display */}
                <div className="credits-display">
                  <span className="credits-amount">{currentUser?.credits || 0}</span>
                  <span className="credits-label">credits</span>
                  {/* Temporary refresh button for testing */}
                  <button
                    onClick={() => {
                      debug('Manual credit refresh triggered');
                      fetchUserFromAPI();
                    }}
                    style={{
                      marginLeft: '8px',
                      padding: '2px 6px',
                      fontSize: '10px',
                      background: '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                    title="Refresh credits"
                  >
                    ↻
                  </button>
                </div>

                {/* Theme toggle removed - dark mode only */}

                {/* User Menu */}
                <div className="user-menu">
                  <div className="user-info">
                    <span className="user-name">{currentUser?.name || currentUser?.email}</span>
                    <button className="logout-btn" onClick={onLogout}>
                      Logout
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="auth-buttons">
                <button className="login-btn" onClick={() => handleNavigation('login')}>
                  Login
                </button>
                <button className="signup-btn" onClick={() => handleNavigation('register')}>
                  Sign Up
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className={`mobile-menu-overlay ${mobileMenuOpen ? 'active' : ''}`}
          onClick={() => setMobileMenuOpen(false)}
        />
      )}
    </>
  );
};

export default Header;